/**
 * voice-v8 — Seite „Morgenreport“ IN der App. (19.10.2026)
 *
 * Martin 09:07: „Alle Einzelheiten“ soll in Vespera bleiben, nicht auf die Admin-Seite führen — die verlässt die App-Hülle
 * (Browser-Navigation: PWA/WebView verliert Chat, Mikro, Sprachsitzung). Darum eine Vollseite wie „Ankermomente“: ein Blatt über
 * dem Chat, reloadfest über `#morgenreport`, kein Neuladen, Aufnahme- und Sprachzustand bleiben unberührt.
 *
 * Daten: GET /api/admin/morgenreport?tag= (server/morgenreport-routen.ts). Der Server lässt nur Konto 1 durch; jedes andere Konto
 * bekommt 403 und sieht hier „nicht verfügbar“ — der Menüpunkt steht ohnehin nur bei Konto 1.
 * Inhalt: shared/morgenreport-ansicht.ts (dieselben Bereiche und Regeln wie das Admin-Protokoll). Alle Texte nur über textContent.
 * Aussehen: style.css (.mrg-*), nur Tokens, keine Inline-Styles.
 *
 * 20.10.2026 (Martin: „Es sieht schrecklich aus“ — er will sie „schön und brauchbar“): ein ruhiger Morgenbericht statt
 * Admin-Tafel. Die Seite beantwortet der Reihe nach: Was ist wichtig? (Das Wichtigste) · Was ist heute dran? (Ziel des Tages,
 * mit dem nächsten Schritt „Ziel mit Schmied besprechen“) · Was wartet auf mich? · Was ist über Nacht passiert? (Kacheln mit
 * Zahl UND Bedeutung). Danach „Als gelesen markieren“, „Alle Einzelheiten“ und die anderen Tage. Inhalt und Quellen unverändert.
 */
import type { MorgenInhalt } from "../../shared/morgenreport.js";
import { GELESEN_EREIGNIS, SEITE_HASH, ZIEL_BESPRECHEN_EREIGNIS, morgenVorlesetext, tagAnzeige } from "../../shared/morgenreport.js";
import {
  ANSICHT_RUHIG,
  ansichtBereiche,
  ansichtKacheln,
  ansichtSignale,
  ansichtWartet,
  tagKurz,
  uhrText,
  wochentag,
  zielBesprechText,
  type AnsichtBereich,
  type AnsichtKachel,
  type AnsichtListe,
  type AnsichtTon,
  type AnsichtWartet,
} from "../../shared/morgenreport-ansicht.js";
import { VORLESE_EREIGNIS, sendeGehoert, sendeGeschlossen } from "./morgenreport-karte.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { buildLoadingHint, buildPageHeader } from "./sheet-ui.js";

export const SEITEN_SCHLUESSEL = SEITE_HASH;
export const SEITEN_ID = "morgenreport-sheet";
export const DETAIL_PFAD = "/api/admin/morgenreport";

export interface MorgenDetail {
  heute: string;
  tage: Array<{ tag: string; erstelltMs: number; gehoert: boolean }>;
  bericht: { tag: string; erstelltMs: number; gehoertMs: number | null; gelesen: boolean; inhalt: MorgenInhalt | null } | null;
}

/** Form-Prüfung der Serverantwort; alles andere: null. Rein. */
export function leseDetail(roh: unknown): MorgenDetail | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (r.ok !== true || typeof r.heute !== "string" || !Array.isArray(r.tage)) return null;
  const tage = r.tage.filter((t): t is MorgenDetail["tage"][number] => !!t && typeof t === "object" && typeof (t as { tag?: unknown }).tag === "string" && typeof (t as { erstelltMs?: unknown }).erstelltMs === "number");
  const b = r.bericht;
  let bericht: MorgenDetail["bericht"] = null;
  if (b && typeof b === "object" && typeof (b as { tag?: unknown }).tag === "string") {
    const bb = b as { tag: string; erstelltMs?: unknown; gehoertMs?: unknown; gelesen?: unknown; inhalt?: unknown };
    const i = bb.inhalt as Partial<MorgenInhalt> | null | undefined;
    const lesbar = !!i && typeof i === "object" && i.version === 1 && Array.isArray(i.kurz) && Array.isArray(i.ausgefallen) && !!i.vespera && !!i.schmied && !!i.team && !!i.neues && !!i.wuensche && !!i.ziel;
    bericht = {
      tag: bb.tag,
      erstelltMs: typeof bb.erstelltMs === "number" ? bb.erstelltMs : 0,
      gehoertMs: typeof bb.gehoertMs === "number" ? bb.gehoertMs : null,
      gelesen: bb.gelesen === true,
      inhalt: lesbar ? (i as MorgenInhalt) : null,
    };
  }
  return { heute: r.heute, tage, bericht };
}

/** Holt einen Tag (ohne Angabe: den neuesten). `null` bei jedem Fehler, auch 401/403. */
export async function holeDetail(tag?: string): Promise<MorgenDetail | null> {
  try {
    const q = tag ? `?tag=${encodeURIComponent(tag)}` : "";
    const r = await fetch(`${DETAIL_PFAD}${q}`, { credentials: "same-origin", cache: "no-store" });
    if (!r.ok) return null;
    return leseDetail(await r.json());
  } catch {
    return null;
  }
}

const MARKE: Record<AnsichtTon, string> = { ok: "●", achtung: "▲", problem: "■", neutral: "○" };

function el<K extends keyof HTMLElementTagNameMap>(doc: Document, tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = doc.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** Ampel: Form UND Wort, nie nur Farbe. */
function ampel(doc: Document, ton: AnsichtTon, wort: string): HTMLElement {
  const a = el(doc, "span", `mrg-ampel mrg-ampel--${ton}`);
  const m = el(doc, "span", "mrg-ampel-marke", MARKE[ton]);
  m.setAttribute("aria-hidden", "true");
  a.append(m, doc.createTextNode(wort));
  return a;
}

function zeilenListe(doc: Document, l: AnsichtListe): HTMLElement {
  const ul = el(doc, "ul", "mrg-zeilen");
  l.zeilen.forEach((z, n) => {
    const li = el(doc, "li", "mrg-zeile");
    if (l.zeige !== undefined && n >= l.zeige) li.hidden = true;
    if (z.vor) li.appendChild(el(doc, "span", "mrg-zeile-vor", z.vor));
    li.appendChild(el(doc, "span", "mrg-zeile-text", z.text));
    if (z.unter) li.appendChild(el(doc, "span", "mrg-zeile-unter", z.unter));
    ul.appendChild(li);
  });
  return ul;
}

function listeBox(doc: Document, l: AnsichtListe): HTMLElement | null {
  if (l.zeilen.length === 0) {
    if (!l.leer) return null;
    const p = el(doc, "p", "mrg-leer");
    p.append(el(doc, "strong", "mrg-leer-satz", l.leer.satz), doc.createTextNode(l.leer.zusatz));
    return p;
  }
  const inhalt = el(doc, "div", "mrg-liste");
  inhalt.appendChild(zeilenListe(doc, l));
  const verborgen = l.zeige !== undefined ? Math.max(0, l.zeilen.length - l.zeige) : 0;
  if (verborgen > 0) {
    const mehr = el(doc, "button", "mrg-mehr", `Mehr anzeigen (${verborgen})`);
    mehr.type = "button";
    mehr.setAttribute("aria-expanded", "false");
    mehr.addEventListener("click", () => {
      const auf = mehr.getAttribute("aria-expanded") !== "true";
      mehr.setAttribute("aria-expanded", String(auf));
      mehr.textContent = auf ? "Weniger anzeigen" : `Mehr anzeigen (${verborgen})`;
      inhalt.querySelectorAll<HTMLElement>(".mrg-zeile").forEach((li, n) => {
        if (l.zeige !== undefined && n >= l.zeige) li.hidden = !auf;
      });
    });
    inhalt.appendChild(mehr);
  }
  if (l.rest) inhalt.appendChild(el(doc, "p", "mrg-hinweis", l.rest));
  if (l.art === "aufklapper") {
    const d = el(doc, "details", "mrg-aufklapp");
    d.appendChild(el(doc, "summary", "mrg-aufklapp-kopf", l.titel ?? ""));
    d.appendChild(inhalt);
    return d;
  }
  return inhalt;
}

/** Eine Karte je Bereich: Titel mit Ampel, Kennzahlen groß, Listen darunter. `ohneKennzahlen`/`ohneAmpel`: die Ziel-Karte sagt sie als Satz. */
export function baueBereich(doc: Document, b: AnsichtBereich, wie: { ohneKennzahlen?: boolean; ohneAmpel?: boolean } = {}): HTMLElement {
  const sek = el(doc, "section", "mrg-bereich");
  sek.id = b.id;
  sek.setAttribute("aria-labelledby", `${b.id}-titel`);
  const kopf = el(doc, "header", "mrg-bereich-kopf");
  const h = el(doc, "h3", "mrg-bereich-titel", b.titel);
  h.id = `${b.id}-titel`;
  kopf.appendChild(h);
  if (!wie.ohneAmpel) kopf.appendChild(ampel(doc, b.ton, b.wort));
  sek.appendChild(kopf);
  if (b.kennzahlen.length && !wie.ohneKennzahlen) {
    const dl = el(doc, "dl", `mrg-kennzahlen${b.dreispaltig ? " mrg-kennzahlen--3" : ""}`);
    for (const k of b.kennzahlen) {
      const d = el(doc, "div", "mrg-kennzahl");
      d.append(el(doc, "dt", "mrg-kennzahl-name", k.name), el(doc, "dd", "mrg-kennzahl-wert", k.wert));
      if (k.hinweis) d.appendChild(el(doc, "dd", "mrg-kennzahl-hinweis", k.hinweis));
      dl.appendChild(d);
    }
    sek.appendChild(dl);
  }
  if (b.paare) {
    const dl = el(doc, "dl", "mrg-paare");
    for (const p of b.paare) {
      const d = el(doc, "div", "mrg-paar");
      d.append(el(doc, "dt", "mrg-paar-name", p.name), el(doc, "dd", "mrg-paar-wert", p.wert));
      dl.appendChild(d);
    }
    sek.appendChild(dl);
  }
  for (const l of b.listen) {
    const box = listeBox(doc, l);
    if (box) sek.appendChild(box);
  }
  return sek;
}

/** „Das Wichtigste“: das Schlimmste zuerst, sonst ruhig. Wartende Fragen stehen bei „Wartet auf dich“, nicht doppelt. */
function standZeile(doc: Document, i: MorgenInhalt): HTMLElement {
  const rang: Record<AnsichtTon, number> = { problem: 0, achtung: 1, neutral: 2, ok: 3 };
  const sig = ansichtSignale(i)
    .filter((x) => x.art !== "fragen")
    .sort((a, b) => rang[a.ton] - rang[b.ton]);
  const z = el(doc, "div", "mrg-stand");
  z.setAttribute("role", "status");
  const erstes = sig[0];
  if (!erstes) {
    z.classList.add("mrg-stand--ok");
    z.append(ampel(doc, "ok", "In Ordnung"), el(doc, "p", "mrg-stand-text", ANSICHT_RUHIG));
    return z;
  }
  z.classList.add(`mrg-stand--${erstes.ton}`);
  z.append(ampel(doc, erstes.ton, erstes.ton === "problem" ? "Problem" : "Achtung"), el(doc, "p", "mrg-stand-text", erstes.text));
  for (const s of sig.slice(1)) z.appendChild(el(doc, "p", "mrg-stand-text mrg-stand-text--zusatz", s.text));
  return z;
}

/** Ein Abschnitt mit leiser Überschrift („Das Wichtigste“, „Wartet auf dich“, „Über Nacht“). */
function abschnitt(doc: Document, id: string, titel: string, klasse: string, ...inhalt: HTMLElement[]): HTMLElement {
  const sek = el(doc, "section", `mrg-abschnitt ${klasse}`);
  const h = el(doc, "h3", "mrg-abschnitt-titel", titel);
  h.id = `${id}-titel`;
  sek.setAttribute("aria-labelledby", h.id);
  sek.append(h, ...inhalt);
  return sek;
}

/** „Wartet auf dich“: je Punkt Marke (Form, nie nur Farbe), Satz und leise Titel darunter. */
function wartetListe(doc: Document, punkte: AnsichtWartet[]): HTMLElement {
  const ul = el(doc, "ul", "mrg-wartet");
  for (const p of punkte) {
    const li = el(doc, "li", `mrg-wartet-punkt mrg-wartet-punkt--${p.ton}`);
    li.id = p.id;
    const m = el(doc, "span", "mrg-wartet-marke", MARKE[p.ton]);
    m.setAttribute("aria-hidden", "true");
    const text = el(doc, "div", "mrg-wartet-inhalt");
    text.appendChild(el(doc, "p", "mrg-wartet-text", p.text));
    if (p.zeilen.length) {
      const zl = el(doc, "ul", "mrg-wartet-zeilen");
      for (const z of p.zeilen) zl.appendChild(el(doc, "li", "mrg-wartet-zeile", z));
      text.appendChild(zl);
    }
    li.append(m, text);
    ul.appendChild(li);
  }
  return ul;
}

/** Die Tage-Leiste: ein Knopf je ANDEREM Tag (der gewählte steht als Datum im Kopf, nie doppelt). Ohne andere Tage: keine Leiste. */
function tageLeiste(doc: Document, d: MorgenDetail, gewaehlt: string, waehle: (tag: string) => void): HTMLElement | null {
  const andere = d.tage.slice(0, 14).filter((t) => t.tag !== gewaehlt);
  if (andere.length === 0) return null;
  const nav = el(doc, "nav", "mrg-tage");
  const titel = el(doc, "span", "mrg-tage-titel", "Andere Tage");
  titel.id = "mrg-tage-titel";
  nav.setAttribute("aria-labelledby", titel.id);
  nav.appendChild(titel);
  const ul = el(doc, "ul", "mrg-tage-liste");
  for (const t of andere) {
    const li = doc.createElement("li");
    const b = el(doc, "button", "mrg-tag", tagKurz(t.tag));
    b.type = "button";
    const zusatz = [t.tag === d.heute ? "heute" : "", t.gehoert ? "vorgelesen" : ""].filter(Boolean).join(", ");
    b.setAttribute("aria-label", `${tagAnzeige(t.tag)}${zusatz ? `, ${zusatz}` : ""}`);
    b.addEventListener("click", () => waehle(t.tag));
    li.appendChild(b);
    ul.appendChild(li);
  }
  nav.appendChild(ul);
  return nav;
}

const metaText = (erstelltMs: number, vorgelesen: boolean): string => `Erzeugt um ${uhrText(erstelltMs)} Uhr · ${vorgelesen ? "vorgelesen" : "noch nicht vorgelesen"}`;

function leiserSatz(doc: Document, text: string, zusatz?: string): HTMLElement {
  const box = el(doc, "div", "mrg-leer-seite");
  box.appendChild(el(doc, "p", "mrg-leer-seite-satz", text));
  if (zusatz) box.appendChild(el(doc, "p", "mrg-leer-seite-zusatz", zusatz));
  return box;
}

/** Eine Kachel „Über Nacht“: Name und Ampel (Form und Wort), die Zahl MIT ihrer Bedeutung, leise Angaben darunter. Farbe nur über Klassen. */
function baueKachel(doc: Document, k: AnsichtKachel): HTMLElement {
  const li = el(doc, "li", `mrg-kachel mrg-kachel--${k.ton}`);
  li.id = k.id;
  const kopf = el(doc, "div", "mrg-kachel-kopf");
  kopf.append(el(doc, "h4", "mrg-kachel-titel", k.titel), ampel(doc, k.ton, k.wort));
  const zeile = el(doc, "p", "mrg-kachel-zeile");
  zeile.append(el(doc, "span", "mrg-kachel-zahl", k.zahl), doc.createTextNode(" "), el(doc, "span", "mrg-kachel-bedeutung", k.bedeutung));
  li.append(kopf, zeile);
  if (k.details.length) li.appendChild(el(doc, "p", "mrg-kachel-details", k.details.join(" · ")));
  return li;
}

function knopf(doc: Document, text: string, klasse: string, tipp: (b: HTMLButtonElement) => void): HTMLButtonElement {
  const b = el(doc, "button", `mrg-knopf ${klasse}`, text);
  b.type = "button";
  b.addEventListener("click", (ev) => {
    if (ev.isTrusted) tipp(b); // nur echte Tipps (kein Skript, kein Modell)
  });
  return b;
}

/** Wie viele Tickets im Ziel des Tages zuerst stehen; der Rest hinter „Mehr anzeigen“. */
export const ZIEL_ZEIGE = 3;

/** Zeichnet die Seite aus den Daten in den Rumpf. Rein bis auf das DOM; wirft nie. */
export function zeichneMorgenSeite(body: HTMLElement, d: MorgenDetail, waehle: (tag: string) => void, beiAktion: { besprechen?: (text: string) => void; gelesen?: (tag: string) => void; gehoert?: (tag: string) => void } = {}): void {
  const doc = body.ownerDocument;
  const teile: HTMLElement[] = [];
  const b = d.bericht;
  if (d.tage.length === 0 || !b) {
    body.replaceChildren(leiserSatz(doc, "Noch kein Report.", "Der erste entsteht jeden Morgen zur eingestellten Uhrzeit. Er steht dann hier und als Karte im Chat."));
    return;
  }
  // Kopf: Wochentag leise, das Datum groß (genau einmal), darunter Uhrzeit und Vorlese-Stand; rechts der Vorlese-Knopf.
  const kopf = el(doc, "header", "mrg-kopf");
  const kopfText = el(doc, "div", "mrg-kopf-text");
  const wt = wochentag(b.tag);
  if (wt) kopfText.appendChild(el(doc, "p", "mrg-kopf-tag", wt));
  kopfText.appendChild(el(doc, "h2", "mrg-kopf-titel", tagAnzeige(b.tag)));
  const meta = el(doc, "p", "mrg-kopf-meta", metaText(b.erstelltMs, b.gehoertMs !== null));
  kopfText.appendChild(meta);
  kopf.appendChild(kopfText);
  teile.push(kopf);
  const inhalt = b.inhalt;
  if (!inhalt) {
    teile.push(leiserSatz(doc, "Dieser Report ist nicht lesbar.", "Die anderen Tage stehen darunter."));
  } else {
    // 1. Das Wichtigste.
    teile.push(abschnitt(doc, "mrg-wichtig", "Das Wichtigste", "mrg-abschnitt--wichtig", standZeile(doc, inhalt)));
    const bereiche = ansichtBereiche(inhalt);
    // 2. Ziel des Tages: die Tickets in Arbeit (die ersten drei sichtbar), darunter der nächste Schritt (nur Text ins Feld, Martin sendet selbst).
    const ziel = bereiche.find((x) => x.id === "m-ziel");
    if (ziel) {
      const gekuerzt: AnsichtBereich = { ...ziel, listen: ziel.listen.map((l) => ({ ...l, zeige: ZIEL_ZEIGE })) };
      const karte = baueBereich(doc, gekuerzt, { ohneKennzahlen: true, ohneAmpel: true }); // „1 Frage wartet“ steht bei „Wartet auf dich“
      karte.classList.add("mrg-bereich--ziel");
      const n = inhalt.ziel.inArbeit.length;
      if (n > 0) karte.querySelector(".mrg-bereich-kopf")?.appendChild(el(doc, "p", "mrg-ziel-unter", `${n} ${n === 1 ? "Ticket" : "Tickets"} in Arbeit`));
      const schritt = el(doc, "div", "mrg-schritt");
      schritt.append(
        knopf(doc, "Ziel mit Schmied besprechen", "mrg-knopf--haupt", () => beiAktion.besprechen?.(zielBesprechText(inhalt))),
        el(doc, "p", "mrg-schritt-hinweis", "Legt einen Vorschlag ins Eingabefeld – du sendest selbst."),
      );
      karte.appendChild(schritt);
      teile.push(karte);
    }
    // 3. Wartet auf dich.
    teile.push(abschnitt(doc, "mrg-wartet", "Wartet auf dich", "mrg-abschnitt--wartet", wartetListe(doc, ansichtWartet(inhalt))));
    // 4. Über Nacht: Kacheln mit Zahl UND Bedeutung.
    const kacheln = el(doc, "ul", "mrg-kacheln");
    for (const k of ansichtKacheln(inhalt)) kacheln.appendChild(baueKachel(doc, k));
    teile.push(abschnitt(doc, "mrg-nacht", "Über Nacht · letzte 24 Stunden", "mrg-abschnitt--nacht", kacheln));
    // 5. Abschluss: nur auf Tipp, ohne Außenwirkung.
    const aktionen = el(doc, "div", "mrg-aktionen");
    const einzel = el(doc, "div", "mrg-einzelheiten");
    einzel.id = "mrg-einzelheiten";
    einzel.hidden = true;
    for (const bereich of bereiche) if (bereich.id !== "m-ziel") einzel.appendChild(baueBereich(doc, bereich));
    const gelesen = knopf(doc, b.gelesen ? "Gelesen" : "Als gelesen markieren", "mrg-knopf--gelesen", (g) => {
      g.disabled = true;
      g.textContent = "Gelesen";
      beiAktion.gelesen?.(b.tag);
    });
    gelesen.disabled = b.gelesen;
    const alle = knopf(doc, "Alle Einzelheiten", "mrg-knopf--leise", () => {
      const auf = einzel.hidden;
      einzel.hidden = !auf;
      alle.setAttribute("aria-expanded", String(auf));
      alle.textContent = auf ? "Einzelheiten ausblenden" : "Alle Einzelheiten";
    });
    alle.setAttribute("aria-expanded", "false");
    alle.setAttribute("aria-controls", einzel.id);
    aktionen.append(gelesen, alle);
    // „Vorlesen“: die gemeinsame Schiene setzt den Knopf in den Platz (im Kopf); gelesen wird nur auf Tipp, vermerkt wird es einmal.
    const vorlesen = el(doc, "span", "mrg-vorlesen");
    vorlesen.setAttribute("role", "group");
    vorlesen.setAttribute("aria-label", "Morgenreport vorlesen");
    let vermerkt = b.gehoertMs !== null;
    vorlesen.addEventListener("click", (ev) => {
      if (!ev.isTrusted || vermerkt) return;
      vermerkt = true;
      meta.textContent = metaText(b.erstelltMs, true);
      beiAktion.gehoert?.(b.tag);
    });
    const vorlesetext = morgenVorlesetext(b.tag, inhalt.kurz);
    if (vorlesetext) {
      kopf.appendChild(vorlesen);
      doc.defaultView?.dispatchEvent(new CustomEvent(VORLESE_EREIGNIS, { detail: { text: vorlesetext, platz: vorlesen } }));
    }
    teile.push(aktionen, einzel);
  }
  const leiste = tageLeiste(doc, d, b.tag, waehle);
  if (leiste) teile.push(leiste);
  body.replaceChildren(...teile);
}

export function closeMorgenreportSeite(): void {
  document.getElementById(SEITEN_ID)?.remove();
  clearPageHash(SEITEN_SCHLUESSEL);
}

export async function openMorgenreportSeite(): Promise<void> {
  if (document.getElementById(SEITEN_ID)) return;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));

  const el0 = document.createElement("div");
  el0.className = "sheet page-sheet mrg-sheet";
  el0.id = SEITEN_ID;
  el0.setAttribute("role", "dialog");
  el0.setAttribute("aria-label", "Morgenreport");
  const card = document.createElement("div");
  card.className = "sheet-card";
  const body = document.createElement("div");
  body.className = "mrg-body page-body";
  // Zurück führt in den Chat (kein Menü-Aufklappen): die Seite wird meist aus der Chat-Karte geöffnet.
  const kopf = buildPageHeader("Morgenreport", closeMorgenreportSeite, { reopenMenu: false });
  card.append(kopf, body);
  el0.appendChild(card);
  document.body.appendChild(el0);
  setPageHash(SEITEN_SCHLUESSEL);

  const lade = async (tag?: string): Promise<void> => {
    body.replaceChildren(buildLoadingHint("Lade Morgenreport …"));
    const d = await holeDetail(tag);
    if (!el0.isConnected) return; // inzwischen geschlossen: nichts anfassen
    if (!d) {
      body.replaceChildren(leiserSatz(document, "Der Morgenreport ließ sich gerade nicht laden.", "Bitte später noch einmal öffnen."));
      return;
    }
    zeichneMorgenSeite(body, d, (t) => void lade(t), {
      besprechen: (text) => {
        window.dispatchEvent(new CustomEvent(ZIEL_BESPRECHEN_EREIGNIS, { detail: { text } }));
        closeMorgenreportSeite();
      },
      gehoert: (t) => void sendeGehoert(t),
      gelesen: (t) => {
        void Promise.resolve(sendeGeschlossen(t)).then(() => window.dispatchEvent(new CustomEvent(GELESEN_EREIGNIS)));
      },
    });
  };
  await lade();
}
